import { useRouter } from "@tanstack/react-router";
import { cn } from "tailwind-variants";
import type { CustomizableLinkUmbBlockPartialElementModel } from "#/_generated/umbraco/delivery-api/models";
import {
	Button,
	LinkButton,
} from "#/_packages/umbrella/features/button/components/button.tsx";
import { tryGetButtonVariant } from "#/_packages/umbrella/features/button/utils";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";
import { tryGetIntent, tryGetSize } from "#/_packages/umbrella/utils";

type CustomizableLinkUmbBlockProps = BlockItemProps & {
	content: CustomizableLinkUmbBlockPartialElementModel;
	onPress?: () => void;
	isPending?: boolean;
};

export default function CustomizableLinkUmbBlock({
	className,
	content,
	onPress,
	isPending,
}: CustomizableLinkUmbBlockProps) {
	const isPreview = !!useRouter({ warn: false })?.options.context.previewBlock;
	const link = getUmbLink(content.properties?.link?.[0], {
		id: content.id,
		requireHref: !onPress,
	});
	const label = link?.label ?? "";
	const href = link?.href;
	const target = link?.target;
	const hideLabel = content.properties?.hideTextLabel ?? false;
	const size =
		content.properties?.size === "fullWidth"
			? "fullWidth"
			: tryGetSize(content.properties?.size);

	const sharedProps = {
		"data-tracking-event": link?.trackingEvent,
		size,
		intent: tryGetIntent(content.properties?.intentColor),
		variant: tryGetButtonVariant(content.properties?.typeVariant),
		displayBlock: content.properties?.displayBlock ?? false,
		hideLabel,
		className: cn(className),
		id: link?.id ?? content.id,
	};

	if (onPress) {
		return (
			<Button
				{...sharedProps}
				type="button"
				onPress={onPress}
				isPending={isPending}
			>
				<Button.Content
					spriteIcon={content.properties?.icon}
					iconPosition={content.properties?.iconPosition}
					label={label}
					hideLabel={hideLabel}
				/>
			</Button>
		);
	}

	if (!href) {
		return isPreview ? (
			<span role="status" className="text-warning">
				{content.properties?.link?.[0]?.title || "Link"}: Link destination
				missing
			</span>
		) : null;
	}

	return (
		<LinkButton
			{...sharedProps}
			href={href}
			target={target}
			noFollow={link?.noFollow ?? false}
		>
			<Button.Content
				spriteIcon={content.properties?.icon}
				iconPosition={content.properties?.iconPosition}
				label={label}
				hideLabel={hideLabel}
			/>
		</LinkButton>
	);
}
